// @flow
import type { ChildrenArray, Element } from 'react';
import React from 'react';
import {
  types as t,
  usePropsValidation,
} from '../../validation/runtime-checks/index.js';
import type { CompilerInjectedProps } from '../../validation/runtime-checks/index.js';
import { Text } from '../../Text.js';
import Image from '../../Image.js';
import { CartesianPlane } from '../../CartesianPlane.js';
import { RSLGlossaryTerm } from './RSLGlossaryTerm.js';
import { illustrationColor } from '../../designSystem.js';
import DraftImage from '../../DraftImage.js';
import { Language, English, Spanish } from '../../languages.js';

export function RSLGlossaryTermsList(props: {|
  children: ChildrenArray<Element<typeof RSLTermDefinition>>,
|}) {
  const { failedValidation } = usePropsValidation(props, RSLGlossaryTermsList);
  if (failedValidation) return null;

  return <div style={{ margin: '25px 0' }}>{props.children}</div>;
}
RSLGlossaryTermsList.spec = {
  children: t.childrenArray(t.elementType(RSLTermDefinition)),
};

export function RSLTermDefinition(props: {|
  children:
    | [
        Element<typeof RSLGlossaryTerm>,
        Element<typeof RSLDefinitionDescription>,
      ]
    | [
        Element<typeof RSLGlossaryTerm>,
        Element<typeof RSLDefinitionDescription>,
        Element<typeof RSLDefinitionExample | typeof RSLDefinitionFigure>,
      ],
|}) {
  const { failedValidation } = usePropsValidation(props, RSLTermDefinition);
  if (failedValidation) return null;
  const cs = React.Children.toArray(props.children);
  const colWidths = cs.length === 2 ? ['25%', '75%'] : ['25%', '50%', '25%'];
  return (
    <div
      style={{
        background: illustrationColor.seashell,
        borderRadius: 4,
        display: 'table',
        tableLayout: 'fixed',
        width: '100%',
        breakInside: 'avoid',
        padding: 20,
        marginBottom: 8,
        marginTop: 8,
      }}
    >
      {cs.map((c, i) => (
        <div
          key={i}
          style={{
            display: 'table-cell',
            overflow: 'hidden',
            verticalAlign: 'top',
            width: colWidths[i],
            color: illustrationColor.shuttleGrey,
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 400,
            paddingRight: i === cs.length - 1 ? 0 : 12,
          }}
        >
          {c}
        </div>
      ))}
    </div>
  );
}
RSLTermDefinition.spec = {
  children: t.unionOfTypes(
    t.tuple([
      t.elementType(RSLGlossaryTerm),
      t.elementType(RSLDefinitionDescription),
    ]),
    t.tuple([
      t.elementType(RSLGlossaryTerm),
      t.elementType(RSLDefinitionDescription),
      t.unionOfTypes(
        t.elementType(RSLDefinitionExample),
        t.elementType(RSLDefinitionFigure),
      ),
    ]),
  ),
};

export function RSLDefinitionDescription(props: {|
  children: ChildrenArray<Element<typeof Text>>,
|}) {
  const { failedValidation } = usePropsValidation(
    props,
    RSLDefinitionDescription,
  );
  if (failedValidation) return null;
  return props.children;
}
RSLDefinitionDescription.spec = {
  children: t.childrenArray(t.elementType(Text)),
};

export function RSLDefinitionExample(props: {|
  children: ChildrenArray<Element<typeof Text>>,
|}) {
  const { failedValidation } = usePropsValidation(props, RSLDefinitionExample);
  if (failedValidation) return null;
  return (
    <>
      <em style={{ display: 'block', fontSize: 'italic' }}>
        <Language>
          <English>Example</English>
          <Spanish>Ejemplo</Spanish>
        </Language>
        :
      </em>
      {props.children}
    </>
  );
}
RSLDefinitionExample.spec = {
  children: t.childrenArray(t.elementType(Text)),
};

export function RSLDefinitionFigure(props: {|
  children: Element<typeof Image> | Element<typeof CartesianPlane>,
  ...CompilerInjectedProps,
|}) {
  const { failedValidation, addErrors, locations } = usePropsValidation(
    props,
    RSLDefinitionFigure,
  );

  React.useEffect(() => {
    if (!props._performValidation || failedValidation) return;
    // $FlowFixMe the withValidation HOC that CartesianPlan is wrapped with means width/height is being lost in the type
    const { width, height } = props.children.props;

    if (width !== 116 || height !== 116) {
      addErrors([
        {
          message: `The figure must have its dimensions set to 116×116. We only want small figures in the term definition.`,
          ...locations.children,
        },
      ]);
    }
  });

  if (failedValidation) return null;
  return props.children;
}
RSLDefinitionFigure.spec = {
  children: t.unionOfTypes(
    t.elementType(Image),
    t.elementType(DraftImage),
    t.elementType(CartesianPlane),
  ),
};
